import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { RoleService } from '../../services/role.service';
import { MenuService } from '../../services/menu.service';
import { Role } from '../../model/role.model';
import { Menu } from '../../model/menu.model';
import { RoleMenuPermission } from '../../model/role-menu-permission.model';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';

@Component({
  selector: 'app-role-edit',
  templateUrl: './role-edit.component.html',
  styleUrls: ['./role-edit.component.css'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
})
export class RoleEditComponent implements OnInit {
  roleForm: FormGroup;
  mode: 'create' | 'edit' | 'view' = 'view'; // default view mode
  roleId: number | null = null;
  menus: Menu[] = [];
  permissions: RoleMenuPermission[] = [];
  createdAt: number | null = null;

  constructor(
    private fb: FormBuilder,
    private roleService: RoleService,
    private menuService: MenuService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.roleForm = this.fb.group({
      roleName: [{ value: '', disabled: true }, Validators.required],
      roleCode: [{ value: '', disabled: true }, [Validators.required, Validators.minLength(3)]],
      createdAt: [{ value: '', disabled: true }]
    });
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  ngOnInit(): void {
    this.roleId = +this.route.snapshot.paramMap.get('id')! || null;
    this.route.queryParams.subscribe(params => {
      this.mode = params['mode'] || (this.roleId ? 'view' : 'create');
    });
    this.loadMenus();
    if (this.roleId) {
      this.loadRole();
    } else {
      this.initEmptyPermissions();
    }
  }

  initEmptyPermissions(): void {
    this.permissions = this.menus.map(menu => ({
      id: 0,
      menu,
      canView: false,
      canEdit: false
    }));
  }

  loadMenus(): void {
    this.menuService.getAllMenus().subscribe({
      next: (data) => {
        this.menus = data;
        if (!this.roleId) this.initEmptyPermissions();
      },
      error: (err) => console.error('Error loading menus', err)
    });
  }



  loadRole(): void {
    if (this.roleId) {
      this.roleService.getRoleById(this.roleId).subscribe({
        next: (role) => {
          this.roleForm.patchValue({
            roleName: role.roleName,
            roleCode: role.roleCode,
            createdAt: this.formatDate(role.createdAt || 0)
          });
          this.createdAt = role.createdAt || null;
          // Only keep canView/canEdit
          this.permissions = role.menuPermissions.map(perm => ({
            id: perm.id || 0,
            menu: perm.menu,
            canView: perm.canView || false,
            canEdit: perm.canEdit || false
          }));
          if (this.mode === 'view') this.roleForm.disable();
        },
        error: (err) => console.error('Error loading role', err)
      });
    }
  }

  enableEdit(): void {
    this.mode = 'edit';
    // this.roleForm.get('roleName')?.enable();
    // this.roleForm.get('roleCode')?.enable();
  }

// role-edit.component.ts (only changes shown)



saveRole(): void {
  // no change except ensure only permissions + original role name/code are sent
  if (!this.roleId) return; // we’re only updating existing role

  const currentDate = new Date().getTime();
  const role: Role = {
    roleId: this.roleId,
    roleName: this.roleForm.get('roleName')!.value,  // still original name
    roleCode: this.roleForm.get('roleCode')!.value,
    menuPermissions: this.permissions,              // updated checkboxes
    createdAt: this.createdAt!,
    updatedAt: currentDate
  };

  this.roleService.updateRole(this.roleId, role).subscribe({
    next: () => {
      window.alert('Permissions updated successfully. Please log out and log in again for the changes to take effect.');
      this.mode = 'view';              // go back to view mode
      this.router.navigate(['user-roles']);
    },
    error: (err) => console.error('Error updating role', err)
  });
}


  updatePermission(menuId: number, type: 'canView' | 'canEdit', value: boolean): void {
    if (this.isViewMode) return;
    const perm = this.permissions.find(p => p.menu.menuId === menuId);
    if (perm) perm[type] = value;
  }

  formatDate(timestamp: number | undefined | null): string {
    if (!timestamp && timestamp !== 0) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }
}
